import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Course } from '../../types';

interface EnrollCourseModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (enrollment: any) => void;
  course: Course | null;
}

const EnrollCourseModal: React.FC<EnrollCourseModalProps> = ({ 
  open, 
  onClose, 
  onSubmit,
  course 
}) => {
  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Básico':
        return 'success';
      case 'Intermedio':
        return 'warning';
      case 'Avanzado':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleSubmit = () => {
    if (!course) return;

    // Crear inscripción
    const newEnrollment = {
      id: `enroll_${Date.now()}`,
      userId: 'user_emprendedor_1', // Usuario actual
      courseId: course.id,
      enrolledAt: new Date().toISOString(),
      progress: 0,
      completedContents: [],
      lastAccessedAt: new Date().toISOString()
    };

    onSubmit(newEnrollment);
    handleClose();
  };

  const handleClose = () => {
    onClose();
  };

  if (!course) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Inscribirse al Curso</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {/* Información del curso */}
          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip 
                label={course.level} 
                size="small" 
                color={getLevelColor(course.level) as any}
              />
              <Chip label={course.category} size="small" variant="outlined" />
            </Box>
            
            <Typography variant="h5" gutterBottom>
              {course.title}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              {course.description}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AccessTimeIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Duración total: {course.duration} minutos
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* Contenido del curso */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Contenido del Curso ({course.contents.length} módulos)
            </Typography>
            <List dense>
              {course.contents.map((content, index) => (
                <ListItem key={content.id}>
                  <ListItemIcon>
                    <PlayCircleOutlineIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${index + 1}. ${content.title}`}
                    secondary={`${content.type} - ${content.duration} min`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          <Divider />

          {/* Beneficios */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Al inscribirte obtendrás:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText primary="Acceso completo a todos los módulos" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText primary="Seguimiento de tu progreso" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText primary="Certificado al completar el curso" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText primary="Acceso de por vida al contenido" />
              </ListItem>
            </List>
          </Box>

          {course.recommendedFor && course.recommendedFor.length > 0 && (
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Recomendado para proyectos en etapa:</strong>{' '}
                {course.recommendedFor.join(', ')}
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Confirmar Inscripción
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnrollCourseModal;
